import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { cn, getInitials, formatNumber, formatTimeAgo } from '../lib/utils';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Users, Settings, Loader2, Search, 
  Lock, Globe, Crown, Shield, UserPlus, UserMinus, 
  MoreVertical, LogOut, Trash2, Edit, Image, Check,
  MessageSquare, Heart, Plus, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Add Member Modal
function AddMemberModal({ open, onClose, communityId, existingMembers, onSuccess }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const searchUsers = async (q) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out existing members
      const filtered = response.data.filter(u => !existingMembers.includes(u.id));
      setResults(filtered);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) return;
    
    setLoading(true);
    try {
      for (const userId of selectedUsers) {
        await axios.post(
          `${API}/communities/${communityId}/members/add`,
          { user_id: userId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(`Added ${selectedUsers.length} member(s)!`);
      onSuccess();
      onClose();
      setSelectedUsers([]);
      setQuery('');
      setResults([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add members');
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Add Members
          </DialogTitle>
          <DialogDescription>
            Search and select users to add to this community
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                searchUsers(e.target.value);
              }}
              className="pl-10"
            />
          </div>
          
          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2 p-2 bg-secondary/30 rounded-lg">
              {selectedUsers.map(userId => {
                const user = results.find(u => u.id === userId);
                return user ? (
                  <div 
                    key={userId}
                    className="flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded-full text-sm"
                  >
                    {user.username}
                    <button onClick={() => toggleUser(userId)} className="ml-1">×</button>
                  </div>
                ) : null;
              })}
            </div>
          )}
          
          {/* Search Results */}
          <div className="max-h-60 overflow-y-auto space-y-1">
            {searching ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : results.length === 0 && query.length >= 2 ? (
              <p className="text-center text-muted-foreground py-4">No users found</p>
            ) : (
              results.map(user => (
                <button
                  key={user.id}
                  onClick={() => toggleUser(user.id)}
                  className={cn(
                    "w-full flex items-center gap-3 p-2 rounded-lg transition-colors",
                    selectedUsers.includes(user.id) 
                      ? "bg-primary/20" 
                      : "hover:bg-secondary"
                  )}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback>{getInitials(user.display_name || user.username)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{user.display_name || user.username}</p>
                    <p className="text-sm text-muted-foreground">@{user.username}</p>
                  </div>
                  {selectedUsers.includes(user.id) && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAddMembers} disabled={loading || selectedUsers.length === 0}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${selectedUsers.length} Member(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CommunityDetailPage() {
  const { communityId } = useParams();
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');
  const [newPostContent, setNewPostContent] = useState('');
  const [posting, setPosting] = useState(false);

  const fetchCommunity = useCallback(async () => {
    try {
      const [commRes, membersRes, postsRes] = await Promise.all([
        axios.get(`${API}/communities/${communityId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/communities/${communityId}/members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/communities/${communityId}/posts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCommunity(commRes.data);
      setMembers(membersRes.data);
      setPosts(postsRes.data);
    } catch (error) {
      console.error('Failed to fetch community:', error);
      if (error.response?.status === 404) {
        toast.error('Community not found');
        navigate('/communities');
      }
    } finally {
      setLoading(false);
    }
  }, [communityId, token, navigate]);

  useEffect(() => {
    fetchCommunity();
  }, [fetchCommunity]);

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) return;
    
    setPosting(true);
    try {
      const response = await axios.post(
        `${API}/communities/${communityId}/posts`,
        { content: newPostContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(prev => [response.data, ...prev]);
      setNewPostContent('');
      toast.success('Post created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleLikePost = async (postId, isLiked) => {
    try {
      if (isLiked) {
        await axios.delete(`${API}/communities/${communityId}/posts/${postId}/like`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/communities/${communityId}/posts/${postId}/like`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setPosts(prev => prev.map(p => 
        p.id === postId 
          ? { ...p, is_liked: !isLiked, likes_count: p.likes_count + (isLiked ? -1 : 1) }
          : p
      ));
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/communities/${communityId}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const handleRemoveMember = async (memberId) => {
    setActionLoading(memberId);
    try {
      await axios.post(
        `${API}/communities/${communityId}/members/remove`,
        { user_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Member removed');
      setMembers(prev => prev.filter(m => m.id !== memberId));
      setCommunity(prev => ({ ...prev, members_count: prev.members_count - 1 }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMakeAdmin = async (memberId) => {
    setActionLoading(memberId);
    try {
      await axios.post(
        `${API}/communities/${communityId}/admins/add`,
        { user_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Admin added');
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, is_admin: true } : m
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make admin');
    } finally {
      setActionLoading(null);
    }
  };

  const handleJoin = async () => {
    setActionLoading('join');
    try {
      await axios.post(
        `${API}/communities/${communityId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Joined community!');
      fetchCommunity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeave = async () => {
    setActionLoading('leave');
    try {
      await axios.post(
        `${API}/communities/${communityId}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Left community');
      navigate('/communities');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this community? This cannot be undone.')) {
      return;
    }
    
    setActionLoading('delete');
    try {
      await axios.delete(
        `${API}/communities/${communityId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Community deleted');
      navigate('/communities');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!community) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="community-detail-page">
      {/* Cover Image */}
      <div 
        className="h-40 bg-gradient-to-r from-primary/30 to-violet-500/30 relative"
        style={community.cover_image ? { 
          backgroundImage: `url(${community.cover_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}}
      >
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute top-4 left-4 bg-black/30 backdrop-blur-sm text-white rounded-full"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        {community.is_admin && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-4 right-4 bg-black/30 backdrop-blur-sm text-white rounded-full"
            onClick={() => setShowSettings(true)}
          >
            <Settings className="w-5 h-5" />
          </Button>
        )}
      </div>

      <main className="max-w-2xl mx-auto px-4 -mt-8 relative z-10">
        {/* Community Info Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold">{community.name}</h1>
                  {community.is_private ? (
                    <Lock className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <Globe className="w-5 h-5 text-green-500" />
                  )}
                </div>
                {community.description && (
                  <p className="text-muted-foreground mt-2">{community.description}</p>
                )}
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {formatNumber(community.members_count)} members
                  </span>
                  {community.is_admin && (
                    <span className="flex items-center gap-1 text-primary">
                      <Shield className="w-4 h-4" />
                      Admin
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                {community.is_member ? (
                  <>
                    {community.is_admin && (
                      <Button 
                        size="sm" 
                        className="rounded-full"
                        onClick={() => setShowAddMember(true)}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Add Member
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-full"
                      onClick={handleLeave}
                      disabled={actionLoading === 'leave'}
                    >
                      {actionLoading === 'leave' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <LogOut className="w-4 h-4 mr-1" />
                          Leave
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <Button 
                    size="sm" 
                    className="rounded-full"
                    onClick={handleJoin}
                    disabled={actionLoading === 'join' || community.is_private}
                  >
                    {actionLoading === 'join' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-1" />
                        Join
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Posts and Members */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-xl bg-secondary/50 p-1">
            <TabsTrigger value="posts" className="rounded-lg">
              <MessageSquare className="w-4 h-4 mr-2" />
              Posts ({posts.length})
            </TabsTrigger>
            <TabsTrigger value="members" className="rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Members ({members.length})
            </TabsTrigger>
          </TabsList>

          {/* Posts Tab */}
          <TabsContent value="posts" className="mt-4 space-y-4">
            {/* Create Post */}
            {community.is_member && (
              <Card>
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback>{getInitials(user.display_name || user.username)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <Textarea
                        placeholder={`Share something with ${community.name}...`}
                        value={newPostContent}
                        onChange={(e) => setNewPostContent(e.target.value)}
                        rows={2}
                        className="resize-none"
                      />
                      <div className="flex justify-end mt-2">
                        <Button
                          onClick={handleCreatePost}
                          disabled={posting || !newPostContent.trim()}
                          size="sm"
                          className="rounded-full"
                        >
                          {posting ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Post
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Posts List */}
            {posts.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground">No posts yet</p>
                {community.is_member && (
                  <p className="text-sm text-muted-foreground/70 mt-1">Be the first to post!</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card key={post.id} className="overflow-hidden">
                    <CardContent className="pt-4">
                      <div className="flex gap-3">
                        <Link to={`/profile/${post.username}`}>
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={post.avatar} />
                            <AvatarFallback>{getInitials(post.display_name || post.username)}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <Link to={`/profile/${post.username}`} className="font-semibold hover:underline">
                                {post.display_name || post.username}
                              </Link>
                              <span className="text-muted-foreground text-sm ml-2">
                                @{post.username} · {formatTimeAgo(post.created_at, language)}
                              </span>
                            </div>
                            {(post.user_id === user.id || community.is_admin) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 h-8 text-destructive"
                                onClick={() => handleDeletePost(post.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <p className="mt-2 whitespace-pre-wrap">{post.content}</p>
                          <div className="flex items-center gap-4 mt-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn("rounded-full", post.is_liked && "text-pink-500")}
                              onClick={() => handleLikePost(post.id, post.is_liked)}
                            >
                              <Heart className={cn("w-4 h-4 mr-1", post.is_liked && "fill-pink-500")} />
                              {post.likes_count}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="mt-4 space-y-4">

          <div className="grid gap-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
                data-testid={`member-${member.id}`}
              >
                <Link to={`/profile/${member.username}`}>
                  <Avatar className="w-12 h-12 ring-2 ring-background">
                    <AvatarImage src={member.avatar} />
                    <AvatarFallback className="bg-primary/20 text-primary">
                      {getInitials(member.display_name || member.username)}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                
                <Link to={`/profile/${member.username}`} className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{member.display_name || member.username}</p>
                    {member.is_creator && (
                      <Crown className="w-4 h-4 text-yellow-500" title="Creator" />
                    )}
                    {member.is_admin && !member.is_creator && (
                      <Shield className="w-4 h-4 text-primary" title="Admin" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">@{member.username}</p>
                </Link>

                {/* Admin actions */}
                {community.is_admin && member.id !== user.id && !member.is_creator && (
                  <div className="flex gap-1">
                    {!member.is_admin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8"
                        onClick={() => handleMakeAdmin(member.id)}
                        disabled={actionLoading === member.id}
                        title="Make Admin"
                      >
                        {actionLoading === member.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Shield className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-destructive hover:text-destructive"
                      onClick={() => handleRemoveMember(member.id)}
                      disabled={actionLoading === member.id}
                      title="Remove Member"
                    >
                      {actionLoading === member.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <UserMinus className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          </TabsContent>
        </Tabs>

        {/* Delete Community (Creator only) */}
        {community.creator_id === user.id && (
          <div className="mt-8 pt-8 border-t border-border">
            <Button
              variant="destructive"
              className="w-full rounded-full"
              onClick={handleDelete}
              disabled={actionLoading === 'delete'}
            >
              {actionLoading === 'delete' ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Community
            </Button>
          </div>
        )}
      </main>

      {/* Add Member Modal */}
      <AddMemberModal
        open={showAddMember}
        onClose={() => setShowAddMember(false)}
        communityId={communityId}
        existingMembers={community.members || []}
        onSuccess={fetchCommunity}
      />
    </div>
  );
}
